import _ from 'lodash';

import { Actor } from 'core/auth/Account';

import * as parser from 'utils/ActorRoleParser';
import { Permission } from 'core/auth/Permission';

export type PermissionContext = {
  readonly actor?: Actor | null;
  readonly agencyAddon?: string[] | null;
  // 會排除掉跟 actor.agencyId 不同經銷商的 actors
  readonly allActors?: Actor[];
};

export interface PermissionAware {
  visible(context: PermissionContext): boolean;
}

export interface ActorPermissionAware {
  visible(currentActor?: Actor | null, allActors?: Actor[]): boolean;
}

export interface AgencyPermissionAware {
  visible(agencyAddon?: string[] | null): boolean;
}

export type ActorRolePermissionDescriptor = { [index: string]: Array<string> };

export class InverseActorPermission implements ActorPermissionAware {
  permission: ActorPermissionAware;

  constructor(permission: ActorPermissionAware) {
    this.permission = permission;
  }

  visible(actor?: Actor | null): boolean {
    return !this.permission.visible(actor);
  }
}

export class ActorRolePermission implements ActorPermissionAware {
  roleDescriptors: ActorRolePermissionDescriptor;

  constructor(roleDescriptors: ActorRolePermissionDescriptor) {
    this.roleDescriptors = roleDescriptors;
  }

  visible(actor?: Actor | null): boolean {
    if (actor) {
      const type: string = parser.typeOf(actor).toLowerCase();
      const role: string = parser.roleOf(actor).toLowerCase();
      const rolePermissions = this.roleDescriptors[type];
      if (rolePermissions) {
        return rolePermissions.includes(role);
      }
    }
    return false;
  }
}

export class HasAllActorRolePermission implements ActorPermissionAware {
  roleDescriptors: ActorRolePermissionDescriptor;

  constructor(roleDescriptors: ActorRolePermissionDescriptor) {
    this.roleDescriptors = roleDescriptors;
  }

  visible(_1?: Actor | null, actors?: Actor[]): boolean {
    if (!actors) {
      return false;
    }
    return Object.keys(this.roleDescriptors).reduce((acc, type) => {
      const matchedActor = actors.find((actor) => {
        const actorType: string = parser.typeOf(actor).toLowerCase();
        const role: string = parser.roleOf(actor).toLowerCase();
        return actorType === type && this.roleDescriptors[type].includes(role);
      });
      return acc && !!matchedActor;
    }, true);
  }
}

export class ActorFuncPermission implements ActorPermissionAware {
  funcPermissions: Array<Permission>;

  constructor(funcPermissions: Array<Permission>) {
    this.funcPermissions = funcPermissions;
  }

  visible(actor?: Actor | null): boolean {
    if (actor) {
      const permissions = actor.permissions;
      if (permissions) {
        return (
          _.intersection(permissions, this.funcPermissions).length ===
          this.funcPermissions.length
        );
      }
    }
    return false;
  }
}

export class AgencyAddOnPermission implements AgencyPermissionAware {
  keys: Array<string>;

  constructor(keys: Array<string>) {
    this.keys = keys;
  }

  visible(agencyAddon?: string[] | null): boolean {
    if (agencyAddon) {
      return _.intersection(agencyAddon, this.keys).length !== 0;
    }
    return false;
  }
}

export class InverseAgencyAddonPermission implements AgencyPermissionAware {
  permission: AgencyPermissionAware;

  constructor(permission: AgencyPermissionAware) {
    this.permission = permission;
  }

  visible(agencyAddon?: string[] | null): boolean {
    return !this.permission.visible(agencyAddon);
  }
}

export class Pmax3OrderPermission implements PermissionAware {
  orderId: number;

  constructor(orderId: number) {
    this.orderId = orderId;
  }

  visible(): boolean {
    return this.orderId >= 2000000;
  }
}

export class InConditionPermission implements PermissionAware {
  constructor(private condition: () => boolean) {}

  visible(): boolean {
    return this.condition();
  }
}

export class PermissionItem implements PermissionAware {
  actorPermission: ActorPermissionAware | null;
  agencyPermission: AgencyPermissionAware | null;
  both: boolean;
  andPermissionAwares: PermissionAware[];
  orPermissionAwares: PermissionAware[];

  constructor(
    actorPermission: ActorPermissionAware | null,
    agencyPermission: AgencyPermissionAware | null = null,
    bothRequired = true
  ) {
    this.actorPermission = actorPermission;
    this.agencyPermission = agencyPermission;
    this.both = bothRequired;
    this.andPermissionAwares = [];
    this.orPermissionAwares = [];
  }

  visible(context: PermissionContext): boolean {
    const actorVisible = this.actorPermission
      ? this.actorPermission.visible(context.actor, context.allActors)
      : true;
    const agencyVisible = this.agencyPermission
      ? this.agencyPermission.visible(context.agencyAddon)
      : true;
    const visible = this.both
      ? actorVisible && agencyVisible
      : actorVisible || agencyVisible;
    const andVisible = this.andPermissionAwares.reduce(
      (visible, permission) => visible && permission.visible(context),
      true
    );
    const orVisible = this.orPermissionAwares.reduce(
      (visible, permission) => visible || permission.visible(context),
      false
    );
    return (visible && andVisible) || orVisible;
  }

  and(permissionItem: PermissionAware) {
    this.andPermissionAwares = _.concat(
      this.andPermissionAwares,
      permissionItem
    );
    return this;
  }

  or(permissionItem: PermissionAware) {
    this.orPermissionAwares = _.concat(this.orPermissionAwares, permissionItem);
    return this;
  }
}

export class PICActorPermission implements ActorPermissionAware {
  visible(actor?: Actor | null): boolean {
    if (actor && actor.agencyId) {
      return [2000025, 2000014].includes(actor.agencyId);
    }
    return false;
  }
}
